<template>
    <div>
        <nav
            class="navbar navbar-fixed-top navbar-fixed-top yamm ms-navbar ms-navbar-dark navbar-mode"
            :style="{
                backgroundColor: teamSettings.navbarColor || '#211941',
            }"
            data-test-id="nav"
        >
            <div class="container-fluid container-full">
                <div class="col-row">
                    <div class="col-md-2">
                        <l-nav-bar-logo
                            :src="teamSettings.logo || '/img/leapsome.png'"
                            :name="teamSettings.name || 'Leapsome'"
                            data-test-id="nav-logo"
                        />
                    </div>
                    <div class="col-md-10">
                        <!-- using v-if as we shouldn't render this if it's not logged in -->
                        <l-nav-bar-typeahead
                            v-if="isLoggedIn"
                            class="navbar-header"
                            :handler="showProfile"
                            data-test-id="nav-typeahead"
                        />
                        <l-nav-bar-menu-top :menu="menu" :logout="logout" />
                        <span
                            v-b-toggle.menu-mobile
                            class="btn-navbar-menu visible-md visible-sm visible-xs"
                        >
                            <i class="fal fa-bars" />
                        </span>
                    </div>
                </div>
            </div>
        </nav>

        <!-- Mobile -->
        <l-nav-bar-menu-mobile :menu="menu.main" :logout="logout" />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LNavBarLogo from "@/components/LNavBarLogo";
import LNavBarMenuMobile from "@/components/LNavBarMenuMobile";
import LNavBarMenuTop from "@/components/LNavBarMenuTop";
import LNavBarTypeahead from "@/components/LNavBarTypeahead";

export default {
    components: {
        LNavBarLogo,
        LNavBarMenuMobile,
        LNavBarMenuTop,
        LNavBarTypeahead,
    },
    // A `data` property is needed so that this file is taken into account by jest's code coverage
    // (cf. https://github.com/vuejs/vue-jest/issues/32)
    //
    data() {
        return {};
    },
    computed: {
        ...mapGetters({
            currentUser: "users/current/user",
            isLoggedIn: "auth/isLoggedIn",
            rights: "users/current/rights",
            teamSettings: "users/current/teamSettings",
        }),
        menu() {
            return {
                main: [
                    {
                        show: this.rights.M_VIEW,
                        href: "#/meetings",
                        text:
                            this.teamSettings.customWording &&
                            this.teamSettings.customWording.checkins
                                ? this.teamSettings.customWording.checkins
                                : this.$t("menu.checkins"),
                        options: {
                            testId: "menu-meetings",
                        },
                    },
                    {
                        show: this.rights.F_VIEW,
                        href: "#/feedback",
                        text:
                            this.teamSettings.customWording &&
                            this.teamSettings.customWording.feedback
                                ? this.teamSettings.customWording.feedback
                                : this.$t("menu.userlist"),
                        options: {
                            testId: "menu-feedback",
                        },
                    },
                    {
                        show: this.rights.G_VIEW,
                        href: "#/goals",
                        text: this.$t("menu.goals"),
                        options: {
                            testId: "menu-goals",
                        },
                    },
                    {
                        show: this.rights.R_VIEW,
                        href: "#/reviews",
                        text:
                            this.teamSettings.customWording &&
                            this.teamSettings.customWording.reviews
                                ? this.teamSettings.customWording.reviews
                                : this.$t("menu.reviews"),
                        options: {
                            testId: "menu-reviews",
                        },
                    },
                    {
                        show: this.rights.S_VIEW,
                        href: "#/surveys",
                        text: this.$t("menu.surveys"),
                        options: {
                            testId: "menu-surveys",
                        },
                    },
                    {
                        show: this.rights.L_VIEW,
                        href: "#/learning",
                        text: this.$t("menu.learning"),
                        options: {
                            testId: "menu-learning",
                        },
                    },
                ],
                settings: [
                    {
                        show: true,
                        href: `#/profile/${this.currentUser?._id}/edit`,
                        text: this.$t("profile.btnEdit"),
                        options: {
                            testId: "menu-profile-settings",
                        },
                    },
                    {
                        show: this.rights.A_FEAT,
                        href: "#/skills",
                        text: this.$t("menu.skills"),
                        options: {
                            testId: "menu-skills",
                        },
                    },
                    {
                        show: !this.rights.A_FEAT && this.rights.P_MATRIX,
                        href: `#/skill/matrix/${this.currentUser?._id}`,
                        text: this.$t("menu.skills"),
                        options: {
                            testId: "menu-skills-matrix",
                        },
                    },
                    {
                        show: this.rights.A_USER,
                        href: "#/team/members",
                        text: this.$t("menu.users"),
                        options: {
                            testId: "menu-users",
                        },
                    },
                    {
                        show: this.rights.A_FEAT,
                        href: "#/team/settings/",
                        text: this.$t("menu.settings"),
                        options: {
                            testId: "menu-settings",
                        },
                    },
                    {
                        show: true,
                        href:
                            this.locale === "de"
                                ? "https://leapsome.zendesk.com/hc/de"
                                : "https://leapsome.zendesk.com/hc/en-us",
                        text: this.$t("menu.support"),
                        options: {
                            external: true,
                            testId: "menu-support",
                        },
                    },
                    {
                        show: true,
                        href:
                            "https://leapsome.zendesk.com/hc/en-us/articles/360004361834-Release-Notes",
                        text: this.$t("menu.whatsNew"),
                        options: {
                            external: true,
                            testId: "menu-release-notes",
                        },
                    },
                    {
                        show: true,
                        href: "https://l00coapatuf.typeform.com/to/jDx9hBPE",
                        text: this.$t("menu.shareProductFeedback"),
                        options: {
                            external: true,
                            testId: "menu-feedback",
                        },
                    },
                ],
                create: [
                    {
                        show: this.rights.F_VIEW && this.rights.F_INSTANT,
                        href: `#/feedback/list/all/create`,
                        text: this.$t("menu.giveFeedback"),
                    },
                    {
                        show: this.rights.F_VIEW && this.rights.F_PRAISE,
                        href: `#/feedback/list/all/create/praise`,
                        text: this.$t("menu.givePraise"),
                    },
                    {
                        show: this.rights.F_VIEW && this.rights.F_INSTANT,
                        href: "#/feedback/list/all/request",
                        text: this.$t("menu.requestFeedback"),
                    },
                    {
                        show: this.rights.F_VIEW,
                        href: `#/feedback/list/all/create?type=privateNote`,
                        text: this.$t("menu.takePrivateNote"),
                    },
                    {
                        show: this.rights.G_VIEW,
                        href: "#/goals/create",
                        text: this.$t("menu.createGoal"),
                    },
                    {
                        show: this.rights.M_VIEW,
                        href: "#/meetings/create",
                        text: this.$t("menu.createMeeting"),
                    },
                    {
                        show: this.rights.S_CREATE,
                        href: "#/surveys/edit/new/basics",
                        text: this.$t("menu.createSurvey"),
                    },
                    {
                        show: this.rights.R_CREATE,
                        href: "#/reviews/templates",
                        text: this.$t("menu.createCycle"),
                    },
                ],
            };
        },
    },
    methods: {
        logout() {
            this.$store.dispatch("auth/logout");
        },
        showProfile(user) {
            this.$router.push(`/profile/${user._id}`);
        },
    },
};
</script>

<style scoped></style>
